import { Dialog, Transition } from '@headlessui/react';
import { ErrorMessage, Field, Form, Formik, FormikTouched, setNestedObjectValues } from 'formik';
import { FC, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { IEvent } from '../../db';
import { createEvent } from '../../db/Clients/event';
import { createID } from '../../utils/common-utils';
import { toast } from '../Alerts';
import { ReactDatePicker } from '../Shared/ReactDatePicker';

interface ICloneEventProps {
  eventToClone: IEvent | null;
  setEventToClone: (event: IEvent | null) => void;
  fetchEvents: () => void;
}

export const CloneEvent: FC<ICloneEventProps> = ({ eventToClone, setEventToClone, fetchEvents }) => {
  const { t } = useTranslation();
  const ICloneEvent = Yup.object({
    eventStartDate: Yup.date().required(t('REQUIRED')),
    eventEndDate: Yup.date(),
    name: Yup.string().required(t('REQUIRED')).default(''),
    entryStartDateTime: Yup.date(),
    entryEndDateTime: Yup.date(),
    cloneMatches: Yup.boolean().required(t('REQUIRED')).default(true),
    cloneParticipants: Yup.boolean().required(t('REQUIRED')).default(false),
    cloneCategories: Yup.boolean().required(t('REQUIRED')).default(false),
    cloneGroups: Yup.boolean().required(t('REQUIRED')).default(false),
    cloneAggs: Yup.boolean().required(t('REQUIRED')).default(false),
    cloneEntryForm: Yup.boolean().required(t('REQUIRED')).default(false),
    cloneTeams: Yup.boolean().required(t('REQUIRED')).default(false),
    hideIt: Yup.boolean().required(t('REQUIRED')).default(true),
  });
  type TCloneEvent = Yup.InferType<typeof ICloneEvent>;

  const cloneEvent = async (values: any, actions: any) => {
    const { cloneMatches, cloneParticipants, cloneCategories, cloneGroups, cloneAggs, cloneEntryForm, cloneTeams, name } = values;
    const eventId = createID(name, 6);

    try {
      // Create a new event object with the cloned data
      const formatDate = (date: Date): string => {
        return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      };

      const newEvent = {
        ...eventToClone,
        _id: eventId,
        name: values.name,
        dates: [formatDate(values.eventStartDate), formatDate(values.eventEndDate)],
        entryDates: [values.entryStartDateTime, values.entryEndDateTime],
        matches: cloneMatches ? eventToClone?.matches : [],
        participants: cloneParticipants ? eventToClone?.participants : [],
        specialCat: cloneCategories ? eventToClone?.specialCat : [],
        groups: cloneGroups ? eventToClone?.groups : [],
        aggs: cloneAggs ? eventToClone?.aggs : [],
        entryForm: cloneEntryForm ? eventToClone?.entryForm : [],
        teams: cloneTeams ? eventToClone?.teams : [],
        hidden: values.hideIt,
      };

      // Call API to create new event with cloned data
      const created = await createEvent(newEvent as IEvent);
      if (created.isSuccess) {
        toast({ title: t('EVENT_CLONED_SUCCESSFULLY'), icon: 'success' });
        fetchEvents(); // Refresh the events list after cloning
      } else {
        toast({ title: t('EVENT_CLONING_FAILED'), icon: 'error' });
      }
      setEventToClone(null);
    } catch (error) {
      console.error(error);
    } finally {
      actions.setSubmitting(false);
    }
  }

  return <Transition appear show={Boolean(eventToClone)} as={Fragment}>
    <Dialog as="div" open={Boolean(eventToClone)} onClose={() => setEventToClone(null)}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0" />
      </Transition.Child>
      <div className="fixed inset-0 bg-[black]/60 z-[999] overflow-y-auto">
        <div className="flex items-start justify-center min-h-screen px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="panel border-0 p-0 rounded-lg w-full max-w-3xl my-8 text-black">
              <>
                <div className="flex bg-[#fbfbfb] items-center justify-between px-5 py-3 rounded-tl-lg rounded-tr-lg">
                  <div className="font-bold text-lg">{t('CONNECT_TO_TEAM_UP_MATE')}</div>
                  <button type="button" onClick={() => setEventToClone(null)} className="text-white-dark hover:text-dark">
                    <i className='bx bx-x text-2xl' />
                  </button>
                </div>
                <Formik
                  initialValues={{
                    name: eventToClone?.name || '',
                    eventStartDate: new Date(),
                    eventEndDate: new Date(),
                    entryStartDateTime: new Date(),
                    entryEndDateTime: new Date(),
                    cloneMatches: true,
                    cloneParticipants: false,
                    cloneCategories: false,
                    cloneGroups: false,
                    cloneAggs: false,
                    cloneEntryForm: false,
                    cloneTeams: false,
                    hideIt: true,
                  }}
                  validationSchema={ICloneEvent}
                  onSubmit={(values: TCloneEvent, actions) => {
                    cloneEvent(values, actions);
                  }}
                >
                  {({ errors, values, touched, handleSubmit, setTouched }) => {

                    useEffect(() => {
                      if (eventToClone) {
                        setTouched({
                          name: true,
                          eventStartDate: true,
                          eventEndDate: true,
                          entryStartDateTime: true,
                          entryEndDateTime: true,
                          cloneMatches: true,
                          cloneParticipants: false,
                          cloneCategories: false,
                          cloneGroups: false,
                          cloneAggs: false,
                          cloneEntryForm: false,
                          cloneTeams: false,
                          hideIt: true,
                        });
                      }
                    }, [eventToClone, setTouched]);

                    return <Form className="space-y-5">
                      <div className="p-5">
                        <div className={errors.name && touched.name ? 'has-error' : ''}>
                          <label htmlFor="name">{t("EVENT_NAME")}</label>
                          <Field name="name" type="text" id="name" placeholder={t("ENTER_EVENT_NAME")} className="form-input" />
                          {errors.name && touched.name && <div className="text-danger mt-1">{t(errors.name === "REQUIRED" ? "NAME_REQUIRED" : errors.name)}</div>}
                        </div>
                        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
                          <div className="mt-4">
                            <label htmlFor="eventStartDate">{t('EVENT_START_DATE')}</label>
                            <ReactDatePicker name='eventStartDate' startDate={values.eventStartDate} endDate={values.eventEndDate} />
                            <ErrorMessage component="div" name="eventStartDate" className="text-danger mt-2" />
                          </div>
                          <div className="mt-4">
                            <label htmlFor="eventEndDate">{t('EVENT_END_DATE')}</label>
                            <ReactDatePicker name='eventEndDate' startDate={values.eventStartDate} endDate={values.eventEndDate} minDate={values.eventStartDate} />
                            <ErrorMessage component="div" name="eventEndDate" className="text-danger mt-2" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
                          <div className="mt-4">
                            <label htmlFor="entryStartDateTime">{t('ENTRIES_START_DATE')}</label>
                            <ReactDatePicker name='entryStartDateTime' showTimeSelect />
                            <ErrorMessage component="div" name="entryStartDateTime" className="text-danger mt-2" />
                          </div>
                          <div className="mt-4">
                            <label htmlFor="entryEndDateTime">{t('ENTRIES_END_DATE')}</label>
                            <ReactDatePicker name='entryEndDateTime' minDate={new Date()} maxDate={values.eventEndDate || values.eventStartDate} showTimeSelect />
                            <ErrorMessage component="div" name="entryEndDateTime" className="text-danger mt-2" />
                          </div>
                        </div>
                        <h2 className='font-semibold font-sans text-lg mt-4'>{t("ALSO_CLONE")}</h2>
                        <div className="grid grid-cols-2 gap-x-4 sm:grid-cols-3 md:grid-cols-4">
                          {eventToClone!.matches?.length! > 0 && <div className="mt-4 flex items-center">
                            <Field
                              name="cloneMatches"
                              type="checkbox"
                              id="cloneMatches"
                              className={`form-checkbox ${errors.cloneMatches && touched.cloneMatches ? 'has-error' : ''}`}
                            />
                            <label className='m-0 text-slate-800' htmlFor="cloneMatches">{t('CLONE_MATCHES')}</label>
                            <ErrorMessage component="div" name="cloneMatches" className="text-danger mt-2" />
                          </div>}
                          {eventToClone!.specialCat?.length! > 0 && <div className="mt-4 flex items-center">
                            <Field
                              name="cloneCategories"
                              type="checkbox"
                              id="cloneCategories"
                              className={`form-checkbox ${errors.cloneCategories && touched.cloneCategories ? 'has-error' : ''}`}
                            />
                            <label className='m-0 text-slate-800' htmlFor="cloneCategories">{t('CLONE_CATEGORIES')}</label>
                            <ErrorMessage component="div" name="cloneCategories" className="text-danger mt-2" />
                          </div>}
                          {eventToClone!.groups?.length! > 0 && <div className="mt-4 flex items-center">
                            <Field
                              name="cloneGroups"
                              type="checkbox"
                              id="cloneGroups"
                              className={`form-checkbox ${errors.cloneGroups && touched.cloneGroups ? 'has-error' : ''}`}
                            />
                            <label className='m-0 text-slate-800' htmlFor="cloneGroups">{t('CLONE_GROUPS')}</label>
                            <ErrorMessage component="div" name="cloneGroups" className="text-danger mt-2" />
                          </div>}
                          {eventToClone!.entryForm?.length! > 0 && <div className="mt-4 flex items-center">
                            <Field
                              name="cloneEntryForm"
                              type="checkbox"
                              id="cloneEntryForm"
                              className={`form-checkbox ${errors.cloneEntryForm && touched.cloneEntryForm ? 'has-error' : ''}`}
                            />
                            <label className='m-0 text-slate-800' htmlFor="cloneEntryForm">{t('CLONE_ENTRY_FORM')}</label>
                            <ErrorMessage component="div" name="cloneEntryForm" className="text-danger mt-2" />
                          </div>}
                          {eventToClone!.aggs?.length! > 0 && <div className="mt-4 flex items-center">
                            <Field
                              name="cloneAggs"
                              type="checkbox"
                              id="cloneAggs"
                              className={`form-checkbox ${errors.cloneAggs && touched.cloneAggs ? 'has-error' : ''}`}
                            />
                            <label className='m-0 text-slate-800' htmlFor="cloneAggs">{t('CLONE_AGGS')}</label>
                            <ErrorMessage component="div" name="cloneAggs" className="text-danger mt-2" />
                          </div>}
                          {eventToClone!.participants?.length! > 0 && <div className="mt-4 flex items-center">
                            <Field
                              name="cloneParticipants"
                              type="checkbox"
                              id="cloneParticipants"
                              className={`form-checkbox ${errors.cloneParticipants && touched.cloneParticipants ? 'has-error' : ''}`}
                            />
                            <label className='m-0 text-slate-800' htmlFor="cloneParticipants">{t('CLONE_PARTICIPANTS')}</label>
                            <ErrorMessage component="div" name="cloneParticipants" className="text-danger mt-2" />
                          </div>}
                          {eventToClone!.teams?.length! > 0 && <div className="mt-4 flex items-center">
                            <Field
                              name="cloneTeams"
                              type="checkbox"
                              id="cloneTeams"
                              className={`form-checkbox ${errors.cloneTeams && touched.cloneTeams ? 'has-error' : ''}`}
                            />
                            <label className='m-0 text-slate-800' htmlFor="cloneTeams">{t('CLONE_TEAMS')}</label>
                            <ErrorMessage component="div" name="cloneTeams" className="text-danger mt-2" />
                          </div>}
                        </div>
                        <div className="mt-4 flex items-center">
                          <Field
                            name="hideIt"
                            type="checkbox"
                            id="hideIt"
                            className={`form-checkbox ${errors.hideIt && touched.hideIt ? 'has-error' : ''}`}
                          />
                          <label className='m-0 text-slate-800' htmlFor="hideIt">{t('HIDE_IT_FOR_NOW')}</label>
                          <ErrorMessage component="div" name="hideIt" className="text-danger mt-2" />
                        </div>
                        <div className="flex justify-end items-center mt-8">
                          <button type="button" onClick={() => setEventToClone(null)} className="underline mx-2">
                            {t('CANCEL')}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (Object.keys(touched).length !== 0 && Object.keys(errors).length === 0) {
                                handleSubmit();
                              } else {
                                setTouched(setNestedObjectValues<FormikTouched<any>>(errors, true));
                              }
                            }}
                            className="btn btn-primary ml-4"
                          >
                            {t('CLONE_EVENT')}
                          </button>
                        </div>
                      </div>
                    </Form>
                  }}
                </Formik>
              </>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </div>
    </Dialog>
  </Transition>;
}